import React, { useState } from 'react';
import { toast } from 'react-toastify';

import SitrepSectionEditDrawer from 'components/DisasterGPT/SitrepSectionEditDrawer';
import { createSectionTemplate } from 'components/DisasterGPT/hooks/useSectionTemplates';
import {
  useRefreshSitrepV2,
  useAddSitrepSectionV2,
  useSitrepSectionAILocks,
  type SitrepSectionType,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import ReportsFileAttachments from 'components/Reports/ReportsFileAttachments';
import ReportsSectionDisplay from 'components/Reports/ReportsSectionDisplay';
import SitrepSectionActionsMenu from 'components/Reports/SitrepSectionActionsMenu';
import { type SitrepLayoutMode } from 'components/Reports/SitrepLayoutSwitcher';
import SitrepSectionDocumentView from 'components/Reports/SitrepSectionDocumentView';
import SitrepSectionTabs, {
  sectionsHaveCategories,
} from 'components/Reports/SitrepSectionTabs';
import SitrepCardShell from 'components/Reports/SitrepCardShell';
import SitrepDraftActionsMenu from 'components/Reports/SitrepDraftActionsMenu';
import ReviewCardActions from 'components/Reports/ReviewCardActions';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import useSitrepSectionActions from 'components/Reports/hooks/useSitrepSectionActions';
import useSitrepSectionSelector from 'components/Reports/hooks/useSitrepSectionSelector';
import {
  SitrepCardSurface,
  type EditableReportsSitrepCardProps,
} from 'components/Reports/reports.types';
import Sentry from '../../instrument';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAppSelector, selectGroupGuid } from 'slices/commonSelectors';

/**
 * Editable SITREP card used on Draft and Review surfaces.
 *
 * ### Draft surface
 *
 * Full editing is always on: add/remove/reorder sections, AI generate, section
 * settings, file attachments, request approval via the header button. There is
 * intentionally no SITREP-wide Save button — each section is saved individually
 * via ReportsSectionDisplay.
 *
 * ### Review surface
 *
 * Approve / reject is the **primary** workflow (always visible). An explicit
 * **"Edit before approving"** toggle lets reviewers make changes before
 * approval when needed. This is intentionally separated from the draft
 * authoring path — the review edit toggle only enables content editing and
 * section settings, but does NOT expose draft-only controls like "Add Section",
 * AI Generate, or file attachments.
 */
const ReportsSitrepCard: React.FC<EditableReportsSitrepCardProps> = ({
  sitrep,
  surface,
}) => {
  const tabSetId = React.useId();
  const isDraft = surface === SitrepCardSurface.Draft;
  const isReview = surface === SitrepCardSurface.Review;
  // A draft that the user has already submitted for review. Still surfaced in
  // the Drafts tab for visibility, but read-only — editing and re-requesting
  // approval belong to the reviewer flow.
  const isPendingReviewDraft = isDraft && sitrep.approval_status === 'PENDING';
  // Edit-mode toggle is review-only: reviewers click "Edit before approving"
  // to switch into an edit state. On the Draft surface authoring is always
  // enabled, so this stays false.
  const [isReviewEditMode, setIsReviewEditMode] = useState(false);
  const [layoutMode, setLayoutMode] = useState<SitrepLayoutMode>('tabs');
  const [isFileAttachExpanded, setIsFileAttachExpanded] = useState(false);

  // ── Data & hooks (all at component top-level) ──────────────────────
  const cardData = useSitrepCardContentData(sitrep.id);
  const { sitrepData, refetch, refreshedAt } = cardData;
  const { activeSectionIndex, activeSection, setActiveSectionIndex } =
    useSitrepSectionSelector(sitrepData?.sections);

  const {
    editSettingsSection,
    setEditSettingsSection,
    handleSaveSettings,
    handleDeleteSection,
    isDeletingSection,
    handleMoveSection,
    canMoveUp,
    canMoveDown,
    handleReorderSections,
  } = useSitrepSectionActions(sitrep.id, sitrepData?.sections, refetch);

  const groupGuid = useAppSelector(selectGroupGuid);
  const addSectionMutation = useAddSitrepSectionV2();
  const refreshSitrepMutation = useRefreshSitrepV2();

  /**
   * Whether the current surface allows draft-only authoring controls (add
   * section, AI generate, file attachments). On for an active Draft; off for a
   * Draft already submitted for review (read-only on the drafter's side) and
   * off for Review (reviewers edit existing content via the toggle, but cannot
   * restructure the report).
   */
  const canUseDraftAuthoringControls = isDraft && !isPendingReviewDraft;

  // Poll for per-section AI generation locks so we can disable the AI
  // Generate Draft button while it's in use. Every visible section's lock is
  // looked up individually since categorized sections all render at once.
  const shouldPollAILocks =
    canUseDraftAuthoringControls &&
    !!sitrepData?.sections?.some((section) => section.type === 'ai');
  const { data: sectionAILocks } = useSitrepSectionAILocks(
    shouldPollAILocks ? sitrep.id : undefined
  );

  // ── Handlers ───────────────────────────────────────────────────────

  const handleAddBlankSection = async () => {
    try {
      const template = await createSectionTemplate(
        {
          name: 'New Section',
          type: 'manual',
          output_format_structured: { summary_options: [] },
        },
        groupGuid
      );
      await addSectionMutation.mutateAsync({
        sitrepId: sitrep.id,
        sectionTemplateId: template.id as UUID,
        selectedDatetime: new Date().toISOString(),
        sitrep_source: sitrep.sitrep_source,
        sitrep_source_id: sitrep.sitrep_source_id,
        order: (sitrepData?.sections?.length ?? 0) + 1,
        aor_id: sitrep.aor_id ?? '',
      });
      const result = await refetch();
      const updatedSections = result.data?.sections;
      if (updatedSections && updatedSections.length > 0) {
        setEditSettingsSection(updatedSections[updatedSections.length - 1]);
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Failed to add section');
    }
  };

  const handleAIGenerateDraft = async (section: SitrepSectionType) => {
    try {
      await refreshSitrepMutation.mutateAsync({
        id: sitrep.id,
        selectedDatetime: new Date().toISOString(),
        sections: [section.id],
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  /**
   * Renders one section's header (name + settings/AI-generate overflow menu)
   * and body. Used both for the single active section (uncategorized templates)
   * and for every section in the active category at once (categorized
   * templates), via `SitrepSectionTabs`'s `renderSection`, as well as
   * `SitrepSectionDocumentView`'s document layout.
   */
  const renderSection = (section: SitrepSectionType) => {
    const isSectionLocked = !!sectionAILocks?.get(section.id);
    const showSettings = canUseDraftAuthoringControls || isReviewEditMode;

    return (
      <>
        <div className="d-flex align-items-center mb-3 gap-2">
          {isSectionLocked ? (
            <Tooltip>
              <TooltipTrigger render={<span className="fw-bold" />}>
                {section.name}
              </TooltipTrigger>
              <TooltipContent side="top">
                Someone else is editing this section right now. Wait until
                they&apos;re finished before making changes.
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="fw-bold">{section.name}</span>
          )}
          {showSettings && (
            <SitrepSectionActionsMenu
              className="ms-auto"
              section={section}
              onOpenSettings={() => setEditSettingsSection(section)}
              onDeleteSection={() => handleDeleteSection(section)}
              onAIGenerate={() => handleAIGenerateDraft(section)}
              isDeleting={isDeletingSection}
              isGeneratingAI={refreshSitrepMutation.isPending}
              isLocked={isSectionLocked}
            />
          )}
        </div>
        <ReportsSectionDisplay
          key={section.id}
          section={section}
          sitrepId={sitrep.id}
          sitrepWorkspaceId={sitrep.incident_id}
          onUpdated={() => refetch()}
          canEdit={canUseDraftAuthoringControls || isReviewEditMode}
        />
      </>
    );
  };

  const hasCategories = sectionsHaveCategories(sitrepData?.sections ?? []);

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <SitrepCardShell
      sitrep={sitrep}
      surface={surface}
      cardData={cardData}
      headerRight={
        <SitrepDraftActionsMenu
          sitrep={sitrep}
          layoutMode={layoutMode}
          onLayoutChange={setLayoutMode}
          canManageDraft={canUseDraftAuthoringControls}
          onAddSection={handleAddBlankSection}
          isAddingSection={addSectionMutation.isPending}
          onAddFiles={() => setIsFileAttachExpanded(true)}
          isAddFilesDisabled={!sitrep.incident_id}
        />
      }
      onCollapse={() => {
        setEditSettingsSection(null);
        setIsReviewEditMode(false);
      }}
    >
      {/* Last-updated timestamp + review-only "Edit before approving" toggle */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        {refreshedAt && (
          <Badge variant="green">
            Last updated: {refreshedAt} by{' '}
            {sitrep.updated_by_user_name ??
              sitrep.created_by_user_name ??
              'Unknown'}
          </Badge>
        )}
        {isReview && (
          <Button
            variant="outline"
            size="icon-sm"
            type="button"
            aria-pressed={isReviewEditMode}
            aria-label={
              isReviewEditMode
                ? 'Done editing before approval'
                : 'Edit before approving'
            }
            title={isReviewEditMode ? 'Done editing' : 'Edit before approving'}
            onClick={() => {
              if (isReviewEditMode) {
                setEditSettingsSection(null);
              }
              setIsReviewEditMode((v) => !v);
            }}
          >
            <SharedIcon
              iconName={isReviewEditMode ? 'check' : 'edit'}
              size="sm"
            />
          </Button>
        )}
      </div>
      {layoutMode === 'tabs' && sitrepData?.sections && (
        <SitrepSectionTabs
          sections={sitrepData.sections}
          activeSectionIndex={activeSectionIndex}
          onSectionChange={setActiveSectionIndex}
          renderSection={renderSection}
          className="mb-3"
          idBase={tabSetId}
        />
      )}
      {canUseDraftAuthoringControls && !sitrepData?.sections?.length && (
        <div className="d-flex">
          <Button
            variant="outline"
            className="ms-auto mb-3"
            type="button"
            disabled={addSectionMutation.isPending}
            onClick={handleAddBlankSection}
          >
            <SharedIcon iconName="add" size="sm" />
            Add First Section
          </Button>
        </div>
      )}

      {layoutMode === 'document' && sitrepData?.sections && (
        <SitrepSectionDocumentView
          sections={sitrepData.sections}
          activeSectionIndex={activeSectionIndex}
          onSectionChange={setActiveSectionIndex}
          renderSection={renderSection}
          onReorderGroups={
            canUseDraftAuthoringControls ? handleReorderSections : undefined
          }
          className="mb-3"
          idBase={tabSetId}
        />
      )}

      {/* Active section toolbar + content. Per-section Edit/Save/Cancel lives
          inside ReportsSectionDisplay — there is intentionally no SITREP-wide
          Save. Categorized templates render this via SitrepSectionTabs above
          instead, for every section in the active category. */}
      {layoutMode === 'tabs' && !hasCategories && activeSection && (
        <div
          id={`${tabSetId}-panel-${activeSectionIndex}`}
          role="tabpanel"
          aria-labelledby={`${tabSetId}-tab-${activeSectionIndex}`}
          className="reports-card__section-content"
        >
          {renderSection(activeSection)}
        </div>
      )}

      {/* File attachments: fully editable while actively drafting; read-only
          once submitted for review or while under review (Review surface
          reviewers view but do not manage attachments here). */}
      <div className="mt-3">
        <ReportsFileAttachments
          sitrepId={sitrep.id}
          workspaceId={sitrep.incident_id}
          readOnly={!canUseDraftAuthoringControls}
          isExpanded={isFileAttachExpanded}
          onExpandedChange={setIsFileAttachExpanded}
        />
      </div>

      {/* Review surface: approve/reject always visible; Done Editing inline when in edit mode */}
      {isReview && (
        <ReviewCardActions
          sitrep={sitrep}
          isEditMode={isReviewEditMode}
          onDoneEditing={
            isReviewEditMode
              ? () => {
                  setEditSettingsSection(null);
                  setIsReviewEditMode(false);
                  refetch();
                }
              : undefined
          }
        />
      )}

      {/* Section settings drawer */}
      {editSettingsSection && (
        <SitrepSectionEditDrawer
          onHide={() => setEditSettingsSection(null)}
          onSave={handleSaveSettings}
          onDelete={() => handleDeleteSection(editSettingsSection)}
          onMoveUp={() => handleMoveSection('up')}
          onMoveDown={() => handleMoveSection('down')}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          section={editSettingsSection}
          sitrepId={sitrep.id}
        />
      )}
    </SitrepCardShell>
  );
};

export default ReportsSitrepCard;
